/**
 * プラグインドメインスキーマ — マニフェスト + ギャラリー API
 */
import { z } from 'zod';
// ========================================
// 基盤値オブジェクト
// ========================================
/**
 * プラグイン名: 小文字英数字とハイフン、1〜64文字
 * スコープ付き名前（publisher/plugin-name）も許可
 */
export const PluginNameSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^(?:[a-z0-9]+(?:-[a-z0-9]+)*\/)?[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Plugin name must be lowercase alphanumeric with hyphens, optionally scoped (e.g. publisher/my-plugin)');
/**
 * semver 形式バージョン文字列
 */
export const SemverSchema = z
    .string()
    .regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/, 'Version must be a valid semver string');
/**
 * プラグインコンポーネント一覧
 */
export const PluginComponentsSchema = z.object({
    skills: z.array(z.string()),
    agents: z.array(z.string()),
    mcpServers: z.array(z.string()),
    externalAgents: z.array(z.string()),
    documents: z.array(z.string()).optional().default([]),
});
/**
 * プラグインコンポーネント数
 */
export const PluginComponentCountsSchema = z.object({
    skills: z.number(),
    agents: z.number(),
    mcpServers: z.number(),
    externalAgents: z.number(),
    documents: z.number().optional().default(0),
});
/**
 * プラグイン作者情報
 */
export const PluginAuthorSchema = z.object({
    email: z.string().email(),
});
// ========================================
// マニフェスト
// ========================================
/**
 * プラグインマニフェスト（plugin.json）
 */
export const PluginManifestSchema = z.object({
    name: PluginNameSchema,
    description: z.string().min(1).max(500),
    version: SemverSchema,
    tags: z.array(z.string().min(1).max(50)).max(20),
    author: PluginAuthorSchema,
    components: PluginComponentsSchema,
});
// ========================================
// プラグインステータス
// ========================================
/**
 * プラグイン公開ステータス
 */
export const PluginStatusSchema = z.enum(['published', 'unpublished']);
/**
 * ソート順
 */
export const PluginSortSchema = z.enum(['newest', 'likes', 'downloads']);
// ========================================
// API リクエスト
// ========================================
/**
 * POST /plugins リクエスト（公開）
 */
export const PublishPluginRequestSchema = z.object({
    pluginId: z.string().min(1),
    name: PluginNameSchema,
    description: z.string().min(1).max(500),
    version: SemverSchema,
    tags: z.array(z.string().min(1).max(50)).max(20),
    releaseNote: z.string().max(2000).optional(),
    s3Key: z.string().min(1),
    components: PluginComponentsSchema,
    readme: z.string().max(50000).optional(),
    containerSandboxEnabled: z.boolean().optional(),
});
/**
 * POST /plugins/upload-url リクエスト
 */
export const PluginUploadUrlRequestSchema = z.object({
    pluginId: z.string().min(1).optional(),
    version: SemverSchema,
    fileName: z
        .string()
        .min(1)
        .refine((name) => !name.includes('..') && !name.includes('/') && !name.includes('\\'), {
        message: 'File name must not contain path traversal characters (.. / \\)',
    })
        .refine((name) => name.endsWith('.zip'), {
        message: 'File name must end with .zip',
    }),
    size: z.number().positive(),
    mimeType: z.string().min(1),
});
/**
 * GET /plugins クエリパラメータ
 */
export const PluginListQuerySchema = z.object({
    search: z.string().optional(),
    tag: z.string().optional(),
    sort: PluginSortSchema.optional().default('newest'),
    page: z.coerce.number().int().positive().optional().default(1),
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
/**
 * PATCH /plugins/{name} リクエスト（非推奨設定/解除）
 */
export const DeprecatePluginRequestSchema = z.discriminatedUnion('deprecated', [
    z.object({
        deprecated: z.literal(true),
        reason: z.string().min(1).max(500),
        successor: PluginNameSchema.optional(),
    }),
    z.object({
        deprecated: z.literal(false),
    }),
]);
// ========================================
// API レスポンス
// ========================================
/**
 * プラグインサマリー（一覧用）
 */
export const PluginSummarySchema = z.object({
    pluginId: z.string(),
    name: z.string(),
    description: z.string(),
    tags: z.array(z.string()),
    ownerEmail: z.string(),
    latestVersion: z.string(),
    downloadCount: z.number(),
    likeCount: z.number(),
    commentCount: z.number().optional(),
    liked: z.boolean(),
    deprecated: z.boolean(),
    containerSandboxEnabled: z.boolean().optional(),
    componentCounts: PluginComponentCountsSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * バージョン情報
 */
export const PluginVersionInfoSchema = z.object({
    version: z.string(),
    releaseNote: z.string().optional(),
    components: PluginComponentsSchema,
    publishedAt: z.string(),
    tags: z.array(z.string()).optional(),
    description: z.string().optional(),
    readme: z.string().optional(),
});
/**
 * プラグイン詳細
 */
export const PluginDetailSchema = PluginSummarySchema.extend({
    readme: z.string(),
    versions: z.array(PluginVersionInfoSchema),
    deprecatedReason: z.string().optional(),
    successorPlugin: z.string().optional(),
});
/**
 * GET /plugins レスポンス
 */
export const PluginListResponseSchema = z.object({
    plugins: z.array(PluginSummarySchema),
    total: z.number(),
    hasMore: z.boolean(),
    nextPage: z.number().optional(),
});
/**
 * GET /plugins/{name} レスポンス
 */
export const PluginDetailResponseSchema = z.object({
    plugin: PluginDetailSchema,
});
/**
 * POST /plugins レスポンス
 */
export const PublishPluginResponseSchema = z.object({
    plugin: PluginDetailSchema,
});
/**
 * POST /plugins/upload-url レスポンス
 */
export const PluginUploadUrlResponseSchema = z.object({
    uploadUrl: z.string(),
    s3Key: z.string(),
    pluginId: z.string(),
});
/**
 * POST /plugins/{name}/download-url レスポンス
 */
export const PluginDownloadUrlResponseSchema = z.object({
    downloadUrl: z.string(),
});
/**
 * POST /plugins/{name}/like レスポンス
 */
export const PluginLikeResponseSchema = z.object({
    liked: z.boolean(),
    likeCount: z.number(),
});
//# sourceMappingURL=plugin-schemas.js.map